import { defaultInputFilterFields, defaultSelectFilterFields } from 'pages/Home/utils'
import { setPosition, setTaskPosition } from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import { getTypeKey, transformTypesToTaskType, updateSort } from 'utils'
import type {
  FilterComponentData,
  InputFilterComponentData,
  SelectFilterComponentData,
  SetSortByPayload,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import type { Dispatch, SetStateAction } from 'react'
import type { PaginatedTableReducerAction } from 'reducers/PaginatedTableReducer/PaginatedTableReducer.d'
import type {
  ConfigGETResponseSuccess,
  ImportsListData,
  ImportsListParams,
  JobsListParams,
  ProductsListData,
  ProductsListParams,
  ProjectsDetailResult,
} from '@contracts'
import type { SortingDetails } from 'components/table/table-types.d'
import type { ExtractElementType } from 'utils/utils.d'

export const defaultProjectSummaryRegionFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: getTypeKey<JobsListParams>('region_ids'),
  labelText: 'Region',
  isMultiSelect: true,
  options: [{ id: 'all', value: 'All' }],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultProjectSummaryRetailerFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: getTypeKey<JobsListParams>('retailers'),
  labelText: 'Retailer',
  isMultiSelect: true,
  options: [{ id: 'all', value: 'All' }],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultProjectSummaryGroupIdFilter: InputFilterComponentData = {
  ...defaultInputFilterFields,
  id: getTypeKey<JobsListParams>('group_ids'),
  labelText: 'Group ID',
  placeholder: 'i.e. 1000000',
}

export const defaultProjectSummaryProductIdFilter: InputFilterComponentData = {
  ...defaultInputFilterFields,
  id: getTypeKey<JobsListParams>('product_ids'),
  labelText: 'Product ID',
  placeholder: 'i.e. 978123456789',
}

export const defaultProjectSummaryTaskTypeFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: getTypeKey<JobsListParams>('types'),
  labelText: 'Task Type',
  isMultiSelect: true,
  options: [
    { id: 'all', value: 'All' },
    { id: 'standard_content', value: transformTypesToTaskType('standard_content') },
    { id: 'retailer_adaption', value: transformTypesToTaskType('retailer_adaption') },
    { id: 'sku_adaption', value: transformTypesToTaskType('sku_adaption') },
  ],
  defaultValue: { id: 'all', value: 'All' },
}

export const defaultProjectSummaryStatusFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: getTypeKey<JobsListParams>('statuses'),
  labelText: 'Status',
  isMultiSelect: true,
  options: [
    { id: 'all', value: 'All' },
    { id: 'blocked', value: 'Blocked' },
    { id: 'executing', value: 'Executing' },
    { id: 'processing', value: 'Processing' },
    { id: 'ready_for_review', value: 'Pending approval' },
    { id: 'completed', value: 'completed' },
    { id: 'failed', value: 'Failed' },
  ],
  defaultValue: { id: 'all', value: 'All' },
}

// PROJECT_IMPORT_LOGS
export const projectSummaryFilterData: FilterComponentData[] = [
  defaultProjectSummaryRegionFilter,
  defaultProjectSummaryRetailerFilter,
  defaultProjectSummaryTaskTypeFilter,
  defaultProjectSummaryStatusFilter,
  defaultProjectSummaryProductIdFilter,
  defaultProjectSummaryGroupIdFilter,
]

const RESET_TABLE_POSITION = 0
// table Handlers
export const refreshHandler = (
  setShouldAppend: Dispatch<SetStateAction<boolean>>,
  tableReducerDispatch: (value: PaginatedTableReducerAction<unknown>) => void,
  apiUrl: URL,
  setApiUrl: Dispatch<SetStateAction<URL>>,
) => {
  setShouldAppend(false)
  tableReducerDispatch(setTaskPosition(RESET_TABLE_POSITION))
  setPosition(apiUrl, setApiUrl, RESET_TABLE_POSITION)
}

export const handleLoadMore = (
  apiData: ImportsListData | ProductsListData | null,
  setShouldAppend: Dispatch<SetStateAction<boolean>>,
  itemCount: number,
  tableReducerDispatch: (value: PaginatedTableReducerAction<unknown>) => void,
  apiUrl: URL,
  setApiUrl: Dispatch<SetStateAction<URL>>,
) => {
  if (!apiData) return
  const { position, item_count } = apiData
  if (item_count === undefined) return console.error('item_count is undefined')
  if (position === undefined) return console.error('position is undefined')

  const newPosition = position + itemCount
  setShouldAppend(true)
  tableReducerDispatch(setTaskPosition(newPosition))
  setPosition(apiUrl, setApiUrl, newPosition)
}

export const handleSortingUpdate = (
  sortData: SortingDetails | undefined,
  setShouldAppend: Dispatch<SetStateAction<boolean>>,
  apiUrl: URL,
  tableReducerDispatch: (value: PaginatedTableReducerAction<unknown>) => void,
  setApiUrl: Dispatch<SetStateAction<URL>>,
) => {
  setShouldAppend(false)

  const sortDataPayload = sortData
    ? ({ column: sortData.column, sort: `${sortData.sort}ending` } as SetSortByPayload<
        NonNullable<ImportsListParams['sort_by'] | ProductsListParams['sort_by']>
      >)
    : null

  const urlSorting = updateSort(apiUrl, sortDataPayload)
  tableReducerDispatch(setTaskPosition(RESET_TABLE_POSITION))
  setApiUrl(urlSorting)
}

export const getActiveRegionRetailers = (
  configData: ConfigGETResponseSuccess | null,
  activeRegionData:
    | ExtractElementType<ProjectsDetailResult['regions']>
    | ExtractElementType<ConfigGETResponseSuccess['regions']>
    | undefined,
) => {
  if (!configData || !activeRegionData) return []
  const configRetailers = configData.retailers
  const retailers = activeRegionData.retailers

  if (!retailers || !configRetailers) return []

  return retailers?.map(retailerId => {
    const findRetailer = configRetailers.find(({ _id }) => _id === retailerId)
    if (findRetailer) return findRetailer
    return { _id: retailerId, name: `Unknown id: ${retailerId}` }
  })
}
